import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";

import useRequest from "@/lib/request/useRequest";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { GrowthModel } from "@/types/dssat";
import { Button, colors } from "@heroui/react";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import {
    Area,
    ComposedChart,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useFormContext } from "react-hook-form";
import { roundTo } from "@/lib/utils/numbers";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { LAIQuantiles } from "@/types/water";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";

function VegetativeTrendline() {
    const { formatDateMMDDYYYY } = useI18n();
    const { t } = useTranslation("insights");
    const { fieldId } = useFieldDashboard() ?? {};
    const { useGetRequest } = useRequest();
    const f = useFormContext();

    const [startDate, endDate] = f.watch(["startDate", "endDate"]);

    const growthModelQuery = useGetRequest<GrowthModel[]>({
        url: `/fields/${fieldId}/growth_model/`,
        enabled: !!fieldId,
        params: {
            start_date: dayjs(startDate).format("YYYY-MM-DD"),
            end_date: dayjs(endDate).format("YYYY-MM-DD"),
        },
    });

    const data = useMemo(() => {
        return sortArrayByKey(growthModelQuery.data ?? [], "capture_date").map(
            (row) => ({
                ...row,
                date: dayjs(row.capture_date).toDate(),
                ndre: [row.ndre_min, row.ndre_max],
                ndvi: [row.ndvi_min, row.ndvi_max],
            }),
        );
    }, [growthModelQuery.data]);

    const { irrigationEnabled } = useFeatureFlag();
    const laiQuery = useGetRequest<LAIQuantiles>({
        url: `/fields/${fieldId}/lai_quantiles/`,
        enabled: !!fieldId,
    });

    const laiData = useMemo(() => {
        return laiQuery.data?.data?.map((row) => ({
            ...row,
            date: dayjs(row.time).toDate(),
        }));
    }, [laiQuery.data]);

    const [key, setKey] = useState<"ndre" | "ndvi" | "lai">("ndre");

    return (
        <div className="flex flex-col gap-2 pb-40">
            <div className="flex gap-2">
                <Button
                    size="sm"
                    color={key === "ndre" ? "primary" : "default"}
                    onPress={() => setKey("ndre")}
                >
                    NDRE
                </Button>
                <Button
                    color={key === "ndvi" ? "primary" : "default"}
                    size="sm"
                    onPress={() => setKey("ndvi")}
                >
                    NDVI
                </Button>
                {irrigationEnabled && (
                    <Button
                        color={key === "lai" ? "primary" : "default"}
                        size="sm"
                        onPress={() => setKey("lai")}
                    >
                        LAI
                    </Button>
                )}
            </div>
            {key === "lai" && (
                <ResponsiveContainer width="100%" height={500}>
                    <LineChart data={laiData} margin={{ bottom: 16, left: 8 }}>
                        <XAxis
                            dataKey="date"
                            scale="time"
                            spacing={50}
                            dy={16}
                            dx={-8}
                            angle={-24}
                            fontSize={12}
                            tickFormatter={(t) => formatDateMMDDYYYY(t)}
                        />
                        <YAxis
                            label={{
                                value: "LAI",
                                angle: -90,
                                position: "insideLeft",
                            }}
                            fontSize={12}
                            domain={["dataMin", "dataMax"]}
                            scale="linear"
                        />
                        <Line
                            dataKey="lai_masked_a"
                            stroke="orange"
                            dot={false}
                            name={t("chartLabels.average", { ns: "insights" })}
                        />
                        <Line
                            dataKey="lai_masked_b"
                            stroke="green"
                            dot={false}
                            name={t("chartLabels.best", { ns: "insights" })}
                        />
                        <Line
                            dataKey="lai_masked_w"
                            stroke="red"
                            dot={false}
                            name={t("chartLabels.worst", { ns: "insights" })}
                        />
                        <Tooltip
                            labelFormatter={(t) => formatDateMMDDYYYY(t)}
                            formatter={(value, name) => {
                                return [roundTo(value as number, 1), name];
                            }}
                        />
                        <Legend
                            verticalAlign="top"
                            height={36}
                            fontSize={12}
                            wrapperStyle={{ fontSize: "12px" }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            )}
            {key !== "lai" && (
                <ResponsiveContainer width="100%" height={500}>
                    <ComposedChart data={data} margin={{ bottom: 16, left: 8 }}>
                        <XAxis
                            dataKey="date"
                            scale="time"
                            spacing={50}
                            dy={16}
                            dx={-8}
                            angle={-24}
                            tickFormatter={(t) => formatDateMMDDYYYY(t)}
                            fontSize={12}
                        />
                        <YAxis
                            label={{
                                value: key === "ndre" ? "NDRE" : "NDVI",
                                angle: -90,
                                position: "insideLeft",
                            }}
                        />
                        <Tooltip
                            labelFormatter={(t) => formatDateMMDDYYYY(t)}
                            formatter={(value, name) => {
                                if (
                                    name === "ndre_mean" ||
                                    name === "ndvi_mean"
                                ) {
                                    return [
                                        roundTo(value as number, 4),
                                        t("chartLabels.mean"),
                                    ];
                                }
                                return [
                                    `${roundTo(Number((value as number[])[0]), 4)} - ${roundTo(Number((value as number[])[1]), 4)}`,
                                    t("chartLabels.minMax"),
                                ];
                            }}
                        />
                        <Area
                            dataKey={key}
                            fill={colors.green[500]}
                            strokeWidth={0}
                        />
                        <Line
                            dataKey={`${key}_mean`}
                            stroke={colors.black}
                            dot={false}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            )}
        </div>
    );
}

export default VegetativeTrendline;
